import { Input as InputBase } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiDownload, FiSearch } from 'react-icons/fi';
import { Botao, Box, Button, Cartao, Flex, Vazio } from './base';
import { celula, filtrar, ordenar, paraCsv } from './formato';
import { Tabela } from './tipos';

const Input = InputBase as React.ComponentType<any>;

const LINHAS_DE_INICIO = 10;

// As tabelas de uma análise num cartão só, com abas: vendedores, clientes,
// produtos e famílias. Busca, ordena e exporta só o que veio do servidor — que
// já é só o que quem está logado pode ver.
export function TabelasDaAnalise({ tabelas, arquivo }: { tabelas: Tabela[]; arquivo: string }) {
  const [nome, setNome] = useState(tabelas[0]?.nome);
  const tabela = tabelas.find((t) => t.nome === nome) || tabelas[0];
  if (!tabela) return null;
  return (
    <Cartao overflow="hidden">
      <Flex
        role="tablist"
        aria-label="Tabelas da análise"
        px="10px"
        pt="8px"
        borderBottom="1px solid var(--pa-linha)"
        overflowX="auto"
        sx={{ gap: '2px' }}
      >
        {tabelas.map((t) => {
          const ativa = t.nome === tabela.nome;
          return (
            <Button
              key={t.nome}
              role="tab"
              aria-selected={ativa}
              variant="ghost"
              size="sm"
              h="36px"
              px="12px"
              borderRadius="8px 8px 0 0"
              fontSize="13px"
              fontWeight={ativa ? 700 : 500}
              color={ativa ? 'var(--pa-tinta)' : 'var(--pa-tinta-3)'}
              borderBottom="2px solid"
              borderColor={ativa ? 'var(--pa-marca)' : 'transparent'}
              _hover={{ color: 'var(--pa-tinta)' }}
              _active={{}}
              flexShrink={0}
              onClick={() => setNome(t.nome)}
            >
              {t.titulo}
            </Button>
          );
        })}
      </Flex>
      <TabelaAnalitica key={tabela.nome} tabela={tabela} arquivo={`${tabela.nome}_${arquivo}`} />
    </Cartao>
  );
}

function TabelaAnalitica({ tabela, arquivo }: { tabela: Tabela; arquivo: string }) {
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<{ campo: string; crescente: boolean } | null>(null);
  const [todas, setTodas] = useState(false);
  const linhas = useMemo(() => {
    const achadas = filtrar(tabela.linhas, busca, tabela.colunas);
    return ordem ? ordenar(achadas, ordem.campo, ordem.crescente) : achadas;
  }, [tabela, busca, ordem]);
  const visiveis = todas ? linhas : linhas.slice(0, LINHAS_DE_INICIO);
  const grade = `minmax(180px, 2fr) repeat(${tabela.colunas.length - 1}, minmax(96px, 1fr))`;

  function exportar() {
    const blob = new Blob([paraCsv(tabela.colunas, linhas)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${arquivo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Box>
      <Flex px="14px" py="10px" align="center" wrap="wrap" sx={{ gap: '8px 12px' }}>
        <Box fontSize="12px" color="var(--pa-tinta-3)" flex="1" minW="200px">
          {tabela.descricao}
        </Box>
        {tabela.linhas.length > 5 && (
          <Flex
            align="center"
            sx={{ gap: '6px' }}
            px="10px"
            h="32px"
            border="1px solid var(--pa-linha)"
            borderRadius="8px"
            bg="var(--pa-superficie)"
            w={{ base: '100%', sm: '220px' }}
          >
            <FiSearch size={14} aria-hidden="true" color="var(--pa-tinta-3)" />
            <Input
              variant="unstyled"
              size="sm"
              fontSize="13px"
              placeholder="Buscar"
              aria-label={`Buscar em ${tabela.titulo}`}
              value={busca}
              onChange={(e: any) => setBusca(e.target.value)}
              color="var(--pa-tinta)"
            />
          </Flex>
        )}
        <Botao leftIcon={<FiDownload />} onClick={exportar} isDisabled={!linhas.length}>
          Exportar
        </Botao>
      </Flex>
      <Box overflowX="auto" borderTop="1px solid var(--pa-linha)">
        <Box minW={`${180 + (tabela.colunas.length - 1) * 110}px`} role="table" aria-label={tabela.titulo}>
          <Box role="row" display="grid" gridTemplateColumns={grade} sx={{ gap: '12px' }} px="14px" py="8px" bg="var(--pa-superficie-2)">
            {tabela.colunas.map((c) => {
              const ativa = ordem?.campo === c.campo;
              const numero = c.unidade !== 'texto';
              return (
                <Box
                  key={c.campo}
                  role="columnheader"
                  aria-sort={ativa ? (ordem!.crescente ? 'ascending' : 'descending') : 'none'}
                  as="button"
                  type="button"
                  onClick={() =>
                    setOrdem(ativa ? { campo: c.campo, crescente: !ordem!.crescente } : { campo: c.campo, crescente: !numero })
                  }
                  display="flex"
                  alignItems="center"
                  justifyContent={numero ? 'flex-end' : 'flex-start'}
                  sx={{ gap: '4px' }}
                  bg="transparent"
                  border="none"
                  p={0}
                  fontSize="12px"
                  fontWeight={600}
                  color={ativa ? 'var(--pa-tinta)' : 'var(--pa-tinta-3)'}
                  cursor="pointer"
                  textAlign={numero ? 'right' : 'left'}
                >
                  {c.rotulo}
                  {ativa && (ordem!.crescente ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />)}
                </Box>
              );
            })}
          </Box>
          {visiveis.map((linha, i) => (
            <Box
              key={i}
              role="row"
              display="grid"
              gridTemplateColumns={grade}
              sx={{ gap: '12px' }}
              px="14px"
              py="9px"
              borderTop="1px solid var(--pa-grade)"
              fontSize="13px"
            >
              {tabela.colunas.map((c) => {
                const valor = linha[c.campo];
                const cor =
                  c.unidade === 'variacao' && typeof valor === 'number'
                    ? valor > 0
                      ? 'var(--pa-sobe)'
                      : valor < 0
                        ? 'var(--pa-desce)'
                        : undefined
                    : undefined;
                return (
                  <Box
                    key={c.campo}
                    role="cell"
                    textAlign={c.unidade === 'texto' ? 'left' : 'right'}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    color={cor}
                    fontWeight={c.unidade === 'texto' ? 500 : 400}
                    title={String(valor ?? '')}
                  >
                    {celula(c.unidade, valor)}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
      {!linhas.length && (
        <Box p="14px">
          <Vazio>Nada encontrado com “{busca}”.</Vazio>
        </Box>
      )}
      {linhas.length > LINHAS_DE_INICIO && (
        <Flex justify="center" py="8px" borderTop="1px solid var(--pa-grade)">
          <Button variant="ghost" size="sm" fontSize="13px" color="var(--pa-tinta-2)" onClick={() => setTodas(!todas)}>
            {todas ? 'Mostrar menos' : `Ver todos os ${linhas.length}`}
          </Button>
        </Flex>
      )}
    </Box>
  );
}
